import { z } from "zod";
import { collectPlaceholderNames } from "../content/helpers.js";
import {
  multilineContentSchema,
  singleLineContentSchema
} from "../content/schema.js";
import {
  checkIsCustomFieldResourceType
} from "./customField.js";
function isMessageTemplateSubject(subject) {
  return singleLineContentSchema.safeParse(subject).success;
}
function isMessageTemplateBody(body) {
  return multilineContentSchema.safeParse(body).success;
}
const MessageTemplateType = {
  Advanced: "advanced",
  Rejected: "rejected",
  NextMeetingInvite: "next_meeting_invite",
  Outreach: "outreach",
  RecordingSummary: "recording_summary",
  RecordingEmail: "recording_email"
};
const allMessageTemplateTypes = [
  MessageTemplateType.Outreach,
  MessageTemplateType.Advanced,
  MessageTemplateType.Rejected,
  MessageTemplateType.NextMeetingInvite,
  MessageTemplateType.RecordingSummary,
  MessageTemplateType.RecordingEmail
];
const messageTemplateTypeSchema = z.enum(allMessageTemplateTypes);
function isMessageTemplateType(value) {
  return messageTemplateTypeSchema.safeParse(value).success;
}
const MessageTemplateTypeToLabel = {
  [MessageTemplateType.Advanced]: "Advancement message",
  [MessageTemplateType.Rejected]: "Rejection message",
  [MessageTemplateType.NextMeetingInvite]: "Next meeting invite",
  [MessageTemplateType.Outreach]: "Outreach message",
  [MessageTemplateType.RecordingSummary]: "Recording summary",
  [MessageTemplateType.RecordingEmail]: "Email"
};
const MessageTemplateVariable = {
  COMPANY_NAME: "company.name",
  CONTACT_FIRST_NAME: "contact.first_name",
  CONTACT_LAST_NAME: "contact.last_name",
  PROJECT_LOCATION: "project.location",
  PROJECT_NEXT_STEPS: "project.next_steps",
  JOB_OPENING_NAME: "project.name",
  JOB_OPENING_PHONE_NUMBER: "project.phone_number",
  INTERVIEW_CALENDAR_EVENT_SUMMARY: "interview.calendar_event.summary",
  INTERVIEW_LINK: "interview.link"
};
const messageTemplateVariableToLabel = {
  [MessageTemplateVariable.COMPANY_NAME]: "Company name",
  [MessageTemplateVariable.CONTACT_FIRST_NAME]: "Contact first name",
  [MessageTemplateVariable.CONTACT_LAST_NAME]: "Contact last name",
  [MessageTemplateVariable.PROJECT_NEXT_STEPS]: "Job opening next steps",
  [MessageTemplateVariable.PROJECT_LOCATION]: "Job opening location",
  [MessageTemplateVariable.JOB_OPENING_NAME]: "Job opening name",
  [MessageTemplateVariable.JOB_OPENING_PHONE_NUMBER]: "Job opening phone number",
  [MessageTemplateVariable.INTERVIEW_CALENDAR_EVENT_SUMMARY]: "Interview calendar event summary",
  [MessageTemplateVariable.INTERVIEW_LINK]: "Interview link"
};
const messageTemplateTypeToVariables = {
  [MessageTemplateType.Advanced]: [
    MessageTemplateVariable.COMPANY_NAME,
    MessageTemplateVariable.CONTACT_FIRST_NAME,
    MessageTemplateVariable.CONTACT_LAST_NAME,
    MessageTemplateVariable.PROJECT_NEXT_STEPS,
    MessageTemplateVariable.JOB_OPENING_NAME,
    MessageTemplateVariable.INTERVIEW_CALENDAR_EVENT_SUMMARY
  ],
  [MessageTemplateType.Rejected]: [
    MessageTemplateVariable.COMPANY_NAME,
    MessageTemplateVariable.CONTACT_FIRST_NAME,
    MessageTemplateVariable.CONTACT_LAST_NAME,
    MessageTemplateVariable.JOB_OPENING_NAME
  ],
  [MessageTemplateType.NextMeetingInvite]: [
    MessageTemplateVariable.COMPANY_NAME,
    MessageTemplateVariable.CONTACT_FIRST_NAME,
    MessageTemplateVariable.CONTACT_LAST_NAME,
    MessageTemplateVariable.PROJECT_LOCATION,
    MessageTemplateVariable.JOB_OPENING_NAME
  ],
  [MessageTemplateType.Outreach]: [
    MessageTemplateVariable.COMPANY_NAME,
    MessageTemplateVariable.CONTACT_FIRST_NAME,
    MessageTemplateVariable.CONTACT_LAST_NAME,
    MessageTemplateVariable.PROJECT_LOCATION,
    MessageTemplateVariable.JOB_OPENING_NAME,
    MessageTemplateVariable.JOB_OPENING_PHONE_NUMBER,
    MessageTemplateVariable.INTERVIEW_LINK
  ],
  [MessageTemplateType.RecordingSummary]: [],
  [MessageTemplateType.RecordingEmail]: []
};
const messageTemplateSettingsSectionTypes = [
  "interview",
  "call_recorder"
];
function isInterviewSettingsSectionType(sectionType) {
  return sectionType === "interview";
}
function isCallRecorderSettingsSectionType(sectionType) {
  return sectionType === "call_recorder";
}
const standardRecordingSummaryTemplateBody = `### Summary
[3-bullet summary of this call, for the purpose of the sales rep to review later]

### Account
[Account name and summary of any other key details about it]

### Contacts
[Bulleted list \u2014 one line per person:
\u2022 **Full name** | Title/Role | Direct phone | Email | Any relevant personal notes to personalize follow-ups (e.g. hobbies, sports teams, noteworthy events etc.) ]

### Discovery & Qualification
[Bulleted list of key facts gathered: size, volume, existing solutions, budget, decision process, timeline, authority, need, etc.]

### Pain Points
[Bulleted list of explicit pains or goals voiced by prospect]

### Value Propositions Mentioned
[Bulleted list of product benefits or differentiators the contact reacted positively to]

### Objections
[Bulleted list of objections, and how the rep responded to each]

### Competitors
[Bulleted list of competitors mentioned, current status, pricing or feature comparisons]

### Next Steps
[Bulleted list of action items in the format:
\u2022 **Owner** | Due date (YYYY-MM-DD) | Task description]
Make sure that if follow up items like a lunch date are discussed to include the date and time,
how many people, lunch orders, allergies etc. Anything that would be important for the follow up.`;
const messageTemplateTypesBySettingsSectionType = {
  interview: [
    MessageTemplateType.Outreach,
    MessageTemplateType.Advanced,
    MessageTemplateType.Rejected
  ],
  call_recorder: [
    MessageTemplateType.RecordingSummary,
    MessageTemplateType.RecordingEmail
  ]
};
const noVariablesMessageTemplateTypes = [
  MessageTemplateType.RecordingSummary,
  MessageTemplateType.RecordingEmail
];
const messageTemplateTypeDescriptions = {
  [MessageTemplateType.Outreach]: {
    subjectLabel: "Subject",
    subject: "Used when sending an email message.",
    body: "Sent via SMS for phone interviews - otherwise sent via email.",
    bodyPlaceholder: void 0
  },
  [MessageTemplateType.Advanced]: {
    subjectLabel: "Subject",
    subject: "Used when sending an email message.",
    body: "Sent via SMS for phone interviews - otherwise sent via email.",
    bodyPlaceholder: void 0
  },
  [MessageTemplateType.Rejected]: {
    subjectLabel: "Subject",
    subject: "Used when sending an email message.",
    body: "Sent via SMS for phone interviews - otherwise sent via email.",
    bodyPlaceholder: void 0
  },
  [MessageTemplateType.NextMeetingInvite]: {
    subjectLabel: void 0,
    subject: void 0,
    body: void 0,
    bodyPlaceholder: void 0
  },
  [MessageTemplateType.RecordingSummary]: {
    subjectLabel: "Name",
    subject: void 0,
    body: "Template of the key details that should be automatically extracted for each recording.",
    bodyPlaceholder: standardRecordingSummaryTemplateBody
  },
  [MessageTemplateType.RecordingEmail]: {
    subjectLabel: "Subject",
    subject: void 0,
    body: void 0,
    bodyPlaceholder: void 0
  }
};
function parseCustomFieldVariable(variable) {
  const [resourceType, domain, fieldName] = variable.split(".");
  if (checkIsCustomFieldResourceType(resourceType) && domain === "custom_field" && fieldName) {
    return {
      resourceType,
      fieldName
    };
  }
  return void 0;
}
function buildCustomFieldVariable(resourceType, fieldName) {
  return `${resourceType}.custom_field.${fieldName}`;
}
function checkMessageTemplateIncludesNextStepsVariable(messageTemplate) {
  for (const variable of collectMessageTemplateVariables(messageTemplate)) {
    if (variable === MessageTemplateVariable.PROJECT_NEXT_STEPS) {
      return true;
    }
  }
  return false;
}
function* collectMessageTemplateVariables(messageTemplate) {
  yield* collectPlaceholderNames(messageTemplate.subject);
  yield* collectPlaceholderNames(messageTemplate.body);
}
export {
  MessageTemplateType,
  MessageTemplateTypeToLabel,
  MessageTemplateVariable,
  allMessageTemplateTypes,
  buildCustomFieldVariable,
  checkMessageTemplateIncludesNextStepsVariable,
  isCallRecorderSettingsSectionType,
  isInterviewSettingsSectionType,
  isMessageTemplateBody,
  isMessageTemplateSubject,
  isMessageTemplateType,
  messageTemplateTypeDescriptions,
  messageTemplateTypeSchema,
  messageTemplateTypeToVariables,
  messageTemplateTypesBySettingsSectionType,
  messageTemplateVariableToLabel,
  noVariablesMessageTemplateTypes,
  parseCustomFieldVariable,
  standardRecordingSummaryTemplateBody
};
//# sourceMappingURL=messageTemplate.js.map
